import {
	AuthenticationData,
	GatewayConstants,
	GatewayErrorCodes,
	LabelTypeEnumeration,
	LogonResult,
	ResultEnumerationData,
} from './Definitions';
import GatewayAuthenticatorBase from './GatewayAuthenticatorBase';
import { Logger } from '../Logger';
import { AotCategory, logConstants } from '../../utils/Constants';
import { GatewayAuthenticationType, ServerInfo } from '../../Common/Definitions';
import FormsGenerator from './FormsGenerator';
import { GatewayLocalisedResources } from './Definitions';
import { RequirementNodeParser } from '../../Common/Parsers/RequirementNodeParser';
import { v4 as uuidv4 } from 'uuid';
import AuthMan from '../AuthMan';
import { QueuedRequest } from '../dsAuth/data/AuthTypes';
import formsAuthWebviewHandler from '../dsAuth/forms/FormsAuthWebViewHandler';
import { gatewayConstants, headerConstants } from '../AuthConstants';
import DSAuth from '../dsAuth/DSAuth';
import { HTTPFetchResponse } from '../../utils/http/Http';
import { HttpStatusCodes } from '../../utils/http/HttpConstants';
import { extractCookieFromResponseHeaders } from '../../AccountConfig/core/servertype/ServerType';
import { primaryTokenStatusConstants } from '../AuthConstants';
import { HeaderParser } from '../dsAuth/parser/HeaderParser';

const CredentialId = {
	Username: 'login',
	Password: 'passwd',
	Submit: 'submit',
	Response: 'response',
	SecondPassword: 'passwd1',
} as const;

enum ShowFormStatus {
	None = 'None',
	Submitted = 'Submitted',
	Cancelled = 'Cancelled',
	Closed = 'Closed',
	Timeout = 'Timeout',
	InvalidForm = 'InvalidForm',
}

const ClientType = {
	WICA: 'wica',
	CVPN: 'cvpn',
	SG: 'sg',
} as const;

interface ShowFormResult {
	status: ShowFormStatus;
	credentials: Map<string, string>;
}

/**
 * @class - Gateway Basic Authenticator class extends from GatewayAuthenticatorBase class
 * TODO - implement the logon() method
 */
export default class GatewayBasicAuthenticator extends GatewayAuthenticatorBase {
	private gatewayUrl: URL;
	private currentChallenge: string;
	private authType: GatewayAuthenticationType;
	private formsGenerator: FormsGenerator;
	private previousUsername: string;
	private passwordCount: Number;
	private invalidCredentialsSupplied: Boolean;
	private authenticationData: AuthenticationData;

	constructor(serverUrl: URL, serverInfo: ServerInfo) {
		super(serverUrl);
		this.gatewayUrl = serverUrl;
		this.previousUsername = '';
		this.passwordCount = serverInfo.gatewayInfo.pwcount;
		this.authType = serverInfo.gatewayInfo.AuthenticationType;
		this.formsGenerator = new FormsGenerator();
		this.currentChallenge = '';
		this.authenticationData = {
			result: LogonResult.Failure,
			authdata: new Map<string, string>(),
		};
		this.invalidCredentialsSupplied = false;
	}

	async logon(): Promise<AuthenticationData> {
		try {
			do {
				const credentials = await this.getCredentials();

				if (credentials.status !== ShowFormStatus.Submitted) {
					Logger.getInstance().write(
						logConstants.INFO,
						`Form status - ${credentials.status}, hence exiting the gateway logon`
					);
					break;
				}

				const postCredentialsResponse = await this.postCredentials(credentials);
				await this.processPostCredentialsResponse(postCredentialsResponse);
			} while (!this.authenticationData.authdata.has(GatewayConstants.NSC_AAAC));

			if (this.authenticationData.authdata.has(GatewayConstants.NSC_AAAC)) {
				this.authenticationData.result = LogonResult.Success;
				formsAuthWebviewHandler.closeForm('', 'success');
			}
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				'Failure in gateway logon: ' + e.message
			);
			formsAuthWebviewHandler.closeForm('', 'error');
			this.authenticationData.result = LogonResult.Failure;
		}

		return this.authenticationData;
	}

	async logoff(): Promise<void> {
		try {
			const headers = {
				'Cache-Control': 'no-cache',
				'X-Citrix-Gateway': this.gatewayUrl.hostname,
				Host: this.gatewayUrl.hostname,
				Connection: 'Close',
			};
			switch (this.authenticationData.authdata.get(GatewayConstants.RewriteRule)) {
				case ClientType.SG: {
					await DSAuth.getInstance().fetch(
						'GET',
						this.gatewayUrl.origin + gatewayConstants.WEB_INTERFACE_LOGOUT,
						headers,
						'',
						this.gatewayUrl.origin
					);
					await DSAuth.getInstance().fetch(
						'GET',
						this.gatewayUrl.origin + gatewayConstants.INDEX_URL,
						headers,
						'',
						this.gatewayUrl.origin
					);
					break;
				}
				case ClientType.CVPN: {
					await DSAuth.getInstance().fetch(
						'GET',
						this.gatewayUrl.origin + gatewayConstants.CVPN_LOGOUT,
						headers,
						'',
						this.gatewayUrl.origin
					);
					break;
				}
				default:
					Logger.getInstance().write(
						logConstants.ERROR,
						`Logoff called with unexpected proxy mode: ${this.authenticationData.authdata.get(
							GatewayConstants.RewriteRule
						)}`
					);
			}
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				'Failure in gateway logoff: ' + e.message
			);
		}
	}

	private async postCredentials(credentials: ShowFormResult) {
		try {
			Logger.getInstance().writeAot(
				AotCategory.Info,
				'Posting credentials to the gateway server for the gateway: ' +
					this.gatewayUrl.hostname
			);
			const path = this.currentChallenge
				? gatewayConstants.CHALLENGE_RESPONSE
				: gatewayConstants.LOGON_URL_BASIC;

			let postData = '';
			credentials.credentials.forEach(function (value, key) {
				postData += `${key}=${value}&`;
			});
			postData = postData.slice(0, -1);

			const postCredentialsResponse = await DSAuth.getInstance().fetch(
				'POST',
				this.gatewayUrl.origin + path,
				{
					'Content-Type': headerConstants.REQUEST_CONTENT_TYPE,
					'Cache-Control': 'no-cache',
					'X-Citrix-Gateway': this.gatewayUrl.hostname,
				},
				postData,
				this.gatewayUrl.origin
			);

			return postCredentialsResponse;
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				'Failure while posting gateway credentials: ' + e.message,
				AotCategory.Error,
				`Store addition failed - Failure while posting gateway credentials to the server for the gateway: ${this.gatewayUrl.hostname} - ${e.message}`
			);
			this.authenticationData.result = LogonResult.Failure;
			throw e;
		}
	}

	private async processPostCredentialsResponse(
		postCredentialsResponse: HTTPFetchResponse
	): Promise<void> {
		try {
			switch (postCredentialsResponse.statusCode) {
				case HttpStatusCodes.HTTP_STATUS_OK: {
					this.extractChallenge(postCredentialsResponse);
					break;
				}
				case HttpStatusCodes.HTTP_STATUS_REDIRECT: {
					this.extractAuthenticationResult(postCredentialsResponse);

					const relativeLocation = HeaderParser.getInstance().extractHeaderValue(
						postCredentialsResponse.headers,
						'Location'
					);
					if (!relativeLocation) {
						throw new Error('302 relative URL location returned from server is missing');
					}

					await DSAuth.getInstance().fetch(
						'GET',
						this.gatewayUrl.origin + relativeLocation,
						{
							'Content-Type': headerConstants.REQUEST_CONTENT_TYPE,
							'Cache-Control': 'no-cache',
							'X-Citrix-Gateway': this.gatewayUrl.hostname,
						},
						'',
						this.gatewayUrl.origin
					);

					if (relativeLocation.includes(GatewayConstants.ProxyModeKey)) {
						const clientType = new URL(
							this.gatewayUrl.origin + relativeLocation
						).search.slice(1);

						if (clientType === ClientType.WICA) {
							this.authenticationData.authdata.set(
								GatewayConstants.RewriteRule,
								ClientType.SG
							);
						} else if (clientType === ClientType.CVPN) {
							this.authenticationData.authdata.set(
								GatewayConstants.RewriteRule,
								ClientType.CVPN
							);
						} else {
							throw new Error(
								`Received an unexpected clientType from the gateway: ${clientType}`
							);
						}
					}

					break;
				}
				default: {
					Logger.getInstance().writeAot(
						AotCategory.Error,
						`Store addition failed - Received unexpected status code from post credentials: ${postCredentialsResponse.statusCode}`
					);
					throw new Error(
						`Unexpected status code from post credentials: ${postCredentialsResponse.statusCode}`
					);
				}
			}
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				'Failure in processing post credentials response: ' + e.message
			);
			this.authenticationData.result = LogonResult.Failure;
			throw e;
		}
	}

	private async extractChallenge(_postCredentialsResponse: HTTPFetchResponse) {
		// ! TODO - Extracting challenge
		return;
	}

	private async extractAuthenticationResult(postCredentialsResponse: HTTPFetchResponse) {
		const sessionCookie = extractCookieFromResponseHeaders(
			postCredentialsResponse.headers,
			GatewayConstants.NSC_AAAC
		);

		if (sessionCookie && sessionCookie.value && !sessionCookie.expires) {
			this.invalidCredentialsSupplied = false;
			this.authenticationData.authdata.set(
				GatewayConstants.NSC_AAAC,
				sessionCookie.value
			);
		} else {
			const errorCookie = extractCookieFromResponseHeaders(
				postCredentialsResponse.headers,
				GatewayConstants.NSC_VPNERR
			);
			if (errorCookie && errorCookie.value && !errorCookie.expires) {
				const errorCode = errorCookie.value;
				if (
					errorCode === GatewayErrorCodes.NoUser ||
					errorCode === GatewayErrorCodes.BadPassword ||
					errorCode === GatewayErrorCodes.InvalidCredentials
				) {
					this.invalidCredentialsSupplied = true;
				} else {
					throw new Error(
						`Gateway response contains NSC_VPNERR cookie with value ${errorCode}`
					);
				}
			} else {
				throw new Error(
					`Gateway response does not contain expected cookies - ${sessionCookie.name} or ${errorCookie.name}`
				);
			}
		}
	}

	private async getCredentials(): Promise<ShowFormResult> {
		try {
			const formXML = this.getFormXML();

			// ! TODO - Handling access denied challenge

			const formsResult = await this.showForm(formXML);

			switch (formsResult.status) {
				case ShowFormStatus.Cancelled:
				case ShowFormStatus.Closed:
				case ShowFormStatus.Timeout:
					this.authenticationData.result = LogonResult.CancelledByUser;
					formsAuthWebviewHandler.closeForm('', 'cancelled');
					break;
				case ShowFormStatus.Submitted: {
					formsResult.credentials.delete(CredentialId.Submit);
					if (formsResult.credentials.has(CredentialId.Username)) {
						this.previousUsername = decodeURIComponent(
							formsResult.credentials.get(CredentialId.Username)
						);
					}
				}
			}

			return formsResult;
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				'Failure in getting credentials: ' + e.message
			);
			this.authenticationData.result = LogonResult.Failure;
			throw e;
		}
	}

	private async showForm(formXML: string): Promise<ShowFormResult> {
		const result: ShowFormResult = {
			status: ShowFormStatus.None,
			credentials: new Map<string, string>(),
		};

		try {
			const requirements = RequirementNodeParser.parseRequirementNodes(formXML);

			const id = uuidv4();
			AuthMan.getInstance().requestQueue.push(
				new QueuedRequest(id, this.gatewayUrl.origin)
			);

			const credentialsResponse = await formsAuthWebviewHandler.showForm(
				id,
				requirements,
				null,
				this.gatewayUrl.origin,
				120
			);

			const requestIndex = AuthMan.getInstance().requestQueue.findIndex(
				(request) => request.id == id
			);
			AuthMan.getInstance().requestQueue.splice(requestIndex, 1);

			if (credentialsResponse === primaryTokenStatusConstants.ERROR_CANCEL_LOGIN) {
				result.status = ShowFormStatus.Cancelled;
			} else {
				result.status = ShowFormStatus.Submitted;

				const delimitCredentialResponse = credentialsResponse.split('&');
				delimitCredentialResponse.forEach(function (element) {
					const [key, value] = element.split('=');
					result.credentials.set(key, value);
				});
			}
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				'Failure during gateway basic showForm: ' + e.message
			);
			this.authenticationData.result = LogonResult.Failure;
			throw e;
		}

		return result;
	}

	private getFormXML(): string {
		Logger.getInstance().write(logConstants.INFO, `Generating form XML for login`);
		let requirementsXML = '';
		let formsResult = ResultEnumerationData.MoreInfo;

		if (!this.currentChallenge) {
			switch (this.authType) {
				case GatewayAuthenticationType.Gateway_AuthType_Domain: {
					Logger.getInstance().write(
						logConstants.INFO,
						`Generating username/password form`
					);
					requirementsXML =
						this.getHeadingXML() +
						this.getUsernameXML() +
						this.getPasswordXML() +
						this.getSecondPasswordXML() +
						this.getErrorMessageXML() +
						this.getSubmitButtonXML();
					break;
				}
				case GatewayAuthenticationType.Gateway_AuthType_RSA: {
					Logger.getInstance().write(
						logConstants.INFO,
						`Generating username/passcode form`
					);
					requirementsXML +=
						this.getHeadingXML() +
						this.getUsernameXML() +
						this.getPasscodeXML() +
						this.getErrorMessageXML() +
						this.getSubmitButtonXML();
					break;
				}
				case GatewayAuthenticationType.Gateway_AuthType_SMS: {
					Logger.getInstance().write(
						logConstants.INFO,
						`Generating SMS (username/password) form`
					);
					requirementsXML +=
						this.getHeadingXML() +
						this.getUsernameXML() +
						this.getPasswordXML() +
						this.getErrorMessageXML() +
						this.getSubmitButtonXML();
					break;
				}
				case GatewayAuthenticationType.Gateway_AuthType_Domain_And_RSA: {
					Logger.getInstance().write(
						logConstants.INFO,
						`Generating username/password/passcode form`
					);
					requirementsXML +=
						this.getHeadingXML() +
						this.getUsernameXML() +
						this.getPasswordXML() +
						this.getPasscodeXML() +
						this.getErrorMessageXML() +
						this.getSubmitButtonXML();
					break;
				}
				default:
					throw new Error('There is no XML for the given authentication type');
			}
		} else {
			Logger.getInstance().write(logConstants.INFO, `Generating a challenge form`);
			formsResult = ResultEnumerationData.UpdateCredentials;
			requirementsXML +=
				this.getHeadingXML() +
				this.getChallengeAndResponseXML() +
				this.getSubmitButtonXML();
		}

		return this.formsGenerator.getFormXML(
			formsResult,
			GatewayLocalisedResources.CANCEL_BUTTON_LABEL,
			requirementsXML,
			120
		);
	}

	private getHeadingXML(): string {
		if (this.currentChallenge) {
			return this.formsGenerator.getHeadingXML(
				GatewayLocalisedResources.CHALLENGE_HEADING
			);
		} else {
			return '';
		}
	}

	private getUsernameXML(): string {
		return this.formsGenerator.getUsernameXML(
			CredentialId.Username,
			GatewayLocalisedResources.USERNAME_LABEL,
			this.previousUsername
		);
	}

	private getPasswordXML(): string {
		const needsSecondPasswordId =
			this.authType === GatewayAuthenticationType.Gateway_AuthType_Domain_And_RSA;
		const credentialId = needsSecondPasswordId
			? CredentialId.SecondPassword
			: CredentialId.Password;
		const dualPassword =
			this.authType === GatewayAuthenticationType.Gateway_AuthType_Domain &&
			this.passwordCount == 2;
		const passwordText = dualPassword
			? GatewayLocalisedResources.PASSWORD1_LABEL
			: GatewayLocalisedResources.PASSWORD_LABEL;

		return this.formsGenerator.getPasswordXML(credentialId, passwordText);
	}

	private getSecondPasswordXML(): string {
		return this.passwordCount == 2
			? this.formsGenerator.getPasswordXML(
					CredentialId.SecondPassword,
					GatewayLocalisedResources.PASSWORD2_LABEL
			  )
			: '';
	}

	private getErrorMessageXML(): string {
		const result = '';

		if (this.invalidCredentialsSupplied) {
			let messageId;
			switch (this.authType) {
				case GatewayAuthenticationType.Gateway_AuthType_SMS: {
					messageId = GatewayLocalisedResources.INVALID_USERNAME_OR_PASSWORD_WARNING;
					break;
				}
				case GatewayAuthenticationType.Gateway_AuthType_Domain_And_RSA: {
					messageId =
						GatewayLocalisedResources.INVALID_USERNAME_OR_PASSWORD_OR_PASSCODE_WARNING;
					break;
				}
				case GatewayAuthenticationType.Gateway_AuthType_RSA: {
					messageId = GatewayLocalisedResources.INVALID_USERNAME_OR_PASSCODE_WARNING;
					break;
				}
				default: {
					messageId = GatewayLocalisedResources.INVALID_USERNAME_OR_PASSWORD_WARNING;
					break;
				}
			}

			return this.formsGenerator.getMessageXML(LabelTypeEnumeration.Error, messageId);
		}

		return result;
	}

	private getPasscodeXML(): string {
		// ! TODO - Implement support for SoftToken
		return this.formsGenerator.getPasscodeXML(
			CredentialId.Password,
			GatewayLocalisedResources.PASSCODE_LABEL
		);
	}

	private getSubmitButtonXML(): string {
		const labelId = this.currentChallenge
			? GatewayLocalisedResources.OK_BUTTON_LABEL
			: GatewayLocalisedResources.LOGON_BUTTON_LABEL;

		return this.formsGenerator.getButtonXML(CredentialId.Submit, labelId);
	}

	private getChallengeAndResponseXML(): string {
		return this.formsGenerator.getTextCredentialXML(
			CredentialId.Response,
			this.currentChallenge
		);
	}

	getGatewayUrl(): URL {
		return this.gatewayUrl;
	}
}
